import {
    type CostCenter,
    type CostCenter2,
    type Employee,
    type ExpenseAccount,
    ExpenseCommentsMentionsEnum,
    ExpenseKindEnum,
    type ExpenseTag,
    type FormDueInDaysEnum,
    RightEnum,
    VendorTypeEnum,
} from "@finway-group/shared/lib/models"
import { ExpenseFilterExportFormatEnum } from "@finway-group/shared/lib/models/expense/expenseFilterExportFormat.enum"
import { Form } from "antd"
import moment from "moment"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { type FilterFormInterface } from "~Components/filter/filterTypes"
import { FormInputEnum, type FormSelectOption } from "~Components/form/form.types"
import { FormMinMax } from "~Components/form/formMinMax"
import { FormMinMaxDate } from "~Components/form/formMinMaxDate"
import { FormMinMaxDateDueDays } from "~Components/form/formMinMaxDateDueDays"
import { FormSelect } from "~Components/form/formSelect"
import { FormSelectTags } from "~Components/form/formSelectTags"
import { ALL_REIMBURSEMENTS_OPTION, expenseStati } from "~Shared/config/consts"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useIsTravelEnabled } from "~Shared/hooks/featureFlags.hooks"
import { useFilterFormCreditorChange } from "~Shared/hooks/filter.hooks"
import { getTableFilterObjectStore } from "~Shared/hooks/table.hooks"
import { AuthzService, UserService } from "~Shared/services"
import {
    createDynamicList,
    createYesOrNoOption,
    getDynamicCostCenter2List,
    getDynamicCostCenterList,
    getDynamicExpenseAccountList,
    getExpenseKindList,
} from "~Shared/utils/filter.utils"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"
import { CollapseWrapper } from "~Shared/wrapper/collapse.wrapper"
import { VendorSelect } from "../expenseForm/commonInputFields/VendorSelect"
import { createExpenseFilterRules } from "../rules/expenseFilter.rules"

export interface ExpenseFilter {
    minAmount?: number
    maxAmount?: number
    status?: string
    billable?: boolean
    kind?: ExpenseKindEnum | typeof ALL_REIMBURSEMENTS_OPTION
    minDate?: Date
    maxDate?: Date
    minInvoiceDate?: Date
    maxInvoiceDate?: Date
    minInvoiceDueDate?: Date
    maxInvoiceDueDate?: Date
    dueInDays?: FormDueInDaysEnum
    dueInDaysRadio?: FormDueInDaysEnum
    minPaidAtDate?: Date
    maxPaidAtDate?: Date
    expenseTags?: Array<ExpenseTag>
    costCenter?: string
    requestedBy?: string
    approvalNeededBy?: string
    vendor?: string
    creditorUser?: string
    exportFormat?: ExpenseFilterExportFormatEnum
    isReimbursement: boolean
    costCenter2?: string
    expenseAccount?: string
    commentsAndMentions?: Array<ExpenseCommentsMentionsEnum>
}

const adjustFilterObject = (filterObject: ExpenseFilter): ExpenseFilter => {
    const fixed = {
        ...filterObject,
        minAmount: filterObject.minAmount ? Number(filterObject.minAmount) : undefined,
        maxAmount: filterObject.maxAmount ? Number(filterObject.maxAmount) : undefined,
        minDate: filterObject.minDate ? moment(filterObject.minDate).startOf("day").toDate() : undefined,
        maxDate: filterObject.maxDate ? moment(filterObject.maxDate).endOf("day").toDate() : undefined,
        minInvoiceDate: filterObject.minInvoiceDate ? moment(filterObject.minInvoiceDate).startOf("day").toDate() : undefined,
        maxInvoiceDate: filterObject.maxInvoiceDate ? moment(filterObject.maxInvoiceDate).endOf("day").toDate() : undefined,
        minInvoiceDueDate: filterObject.minInvoiceDueDate ? moment(filterObject.minInvoiceDueDate).startOf("day").toDate() : undefined,
        maxInvoiceDueDate: filterObject.maxInvoiceDueDate ? moment(filterObject.maxInvoiceDueDate).endOf("day").toDate() : undefined,
        minPaidAtDate: filterObject.minPaidAtDate ? moment(filterObject.minPaidAtDate).startOf("day").toDate() : undefined,
        maxPaidAtDate: filterObject.maxPaidAtDate ? moment(filterObject.maxPaidAtDate).endOf("day").toDate() : undefined,
        expenseTags: filterObject.expenseTags || [],
        billable: filterObject.billable,
        commentsAndMentions: filterObject.commentsAndMentions?.length ? filterObject.commentsAndMentions : undefined,
    }

    if (filterObject?.kind) {
        if (
            Array.isArray(filterObject.kind) &&
            [ExpenseKindEnum.ONE_TIME_EXPENSE, ExpenseKindEnum.MILEAGE, ExpenseKindEnum.HOSPITALITY, ExpenseKindEnum.PER_DIEM].every(
                (kind) => filterObject.kind && filterObject.kind.includes(kind),
            )
        ) {
            fixed.kind = ALL_REIMBURSEMENTS_OPTION
        } else if (filterObject?.kind === ExpenseKindEnum.ONE_TIME_EXPENSE) {
            if (filterObject.isReimbursement) {
                fixed.kind = ExpenseKindEnum.STANDARD
            } else {
                fixed.kind = ExpenseKindEnum.ONE_TIME_EXPENSE
            }
        }
    }

    return fixed
}

export const ExpenseFilterForm = ({ table, formInstance, options, onSubmit }: FilterFormInterface) => {
    const { t } = useTranslation()
    const tableFilterObject = getTableFilterObjectStore<ExpenseFilter>(table)
    const [expenseFilter, setExpenseFilter] = useStateIfMounted(adjustFilterObject(tableFilterObject))
    const rules = createExpenseFilterRules(formInstance, table)
    const employeeList = UserService.getApproverEmployeeList(options.employees as Array<Employee>)
    const isTravelEnabled = useIsTravelEnabled()
    const expenseKindList = getExpenseKindList(isTravelEnabled)
    const { showCostCentersIds } = useCompany()
    const { handleFinish, onCreditorChange } = useFilterFormCreditorChange(expenseFilter, setExpenseFilter, onSubmit)
    const isVendorFilterSelectActive = !!expenseFilter?.vendor || !!expenseFilter?.creditorUser

    useEffect(() => {
        formInstance.resetFields()
        setExpenseFilter(adjustFilterObject(tableFilterObject))
    }, [])

    const statusOptions: Array<FormSelectOption> = expenseStati.map((status: string, index: number) => ({
        param: t(status),
        value: index.toString(),
        index,
    }))

    const commentsAndMentionsOptions: Array<FormSelectOption> = createDynamicList("input:expense_filter.comments_and_mentions", [
        ExpenseCommentsMentionsEnum.COMMENTS,
        ExpenseCommentsMentionsEnum.MENTIONS,
    ])

    const exportOptions: Array<FormSelectOption> = createDynamicList("input:expense_filter.export_format", [
        ExpenseFilterExportFormatEnum.CSV,
        ExpenseFilterExportFormatEnum.NOT_CSV,
        ExpenseFilterExportFormatEnum.DATEV_XML,
        ExpenseFilterExportFormatEnum.NOT_DATEV_XML,
        ExpenseFilterExportFormatEnum.DATEV_INVOICE_CSV,
        ExpenseFilterExportFormatEnum.NOT_DATEV_INVOICE_CSV,
        ExpenseFilterExportFormatEnum.DATEV_ONLINE,
        ExpenseFilterExportFormatEnum.NOT_DATEV_ONLINE,
        ExpenseFilterExportFormatEnum.ALL_DATEV,
        ExpenseFilterExportFormatEnum.NOT_ALL_DATEV,
    ])

    return (
        <Form id="expenseFilterForm" className="form--filter" form={formInstance} onFinish={handleFinish}>
            <FormMinMax
                header={t("input:filter_form.amount")}
                inputType={FormInputEnum.NUMBER}
                min={{
                    name: "minAmount",
                    label: t("input:filter_form.min_amount"),
                    placeholder: t("placeholder:expense_filter.min_amount"),
                    onChange: () => formInstance.validateFields(["minAmount", "maxAmount"]),
                    initialValue: expenseFilter.minAmount,
                    rules: rules.minAmount,
                }}
                max={{
                    name: "maxAmount",
                    label: t("input:filter_form.max_amount"),
                    placeholder: t("placeholder:expense_filter.max_amount"),
                    onChange: () => formInstance.validateFields(["minAmount", "maxAmount"]),
                    initialValue: expenseFilter.maxAmount,
                    rules: rules.maxAmount,
                }}
            />

            <FormSelect
                name="billable"
                header={t("input:expense_filter.billable.title")}
                initialValue={expenseFilter.billable?.toString()}
                options={createYesOrNoOption()}
                isCollapse
                fallback
            />

            <FormSelect name="status" header={t("input:expense_filter.status")} initialValue={expenseFilter.status} options={statusOptions} isCollapse fallback />

            <FormSelect
                name="commentsAndMentions"
                header={t("input:expense_filter.comments_and_mentions.title")}
                initialValue={expenseFilter.commentsAndMentions}
                options={commentsAndMentionsOptions}
                isCollapse
                mode="multiple"
            />

            <FormSelect name="kind" header={t("input:expense_filter.kind")} initialValue={expenseFilter.kind} options={expenseKindList} isCollapse fallback />

            <FormMinMaxDate
                header={t("input:expense_filter.purchase_date_range")}
                min={{
                    name: "minDate",
                    label: t("input:expense_filter.min_date"),
                    placeholder: t("placeholder:expense_filter.min_date"),
                    initialValue: expenseFilter.minDate,
                    rules: rules.minDate,
                }}
                max={{
                    name: "maxDate",
                    label: t("input:expense_filter.max_date"),
                    placeholder: t("placeholder:expense_filter.max_date"),
                    initialValue: expenseFilter.maxDate,
                    rules: rules.maxDate,
                }}
                formInstance={formInstance}
            />

            <FormMinMaxDate
                header={t("input:expense_filter.invoice_date_range")}
                min={{
                    name: "minInvoiceDate",
                    label: t("input:expense_filter.min_date"),
                    placeholder: t("placeholder:expense_filter.min_date"),
                    initialValue: expenseFilter.minInvoiceDate,
                }}
                max={{
                    name: "maxInvoiceDate",
                    label: t("input:expense_filter.max_date"),
                    placeholder: t("placeholder:expense_filter.max_date"),
                    initialValue: expenseFilter.maxInvoiceDate,
                }}
                formInstance={formInstance}
            />

            <FormMinMaxDateDueDays
                header={t("input:filter_form.due_date")}
                dueInDaysSelected={expenseFilter.dueInDaysRadio}
                min={{
                    name: "minInvoiceDueDate",
                    label: t("input:expense_filter.min_date"),
                    placeholder: t("placeholder:expense_filter.min_date"),
                    initialValue: expenseFilter.minInvoiceDueDate,
                    onChange: () => formInstance.validateFields(["minInvoiceDueDate", "maxInvoiceDueDate"]),
                }}
                max={{
                    name: "maxInvoiceDueDate",
                    label: t("input:expense_filter.max_date"),
                    placeholder: t("placeholder:expense_filter.max_date"),
                    initialValue: expenseFilter.maxInvoiceDueDate,
                    onChange: () => formInstance.validateFields(["minInvoiceDueDate", "maxInvoiceDueDate"]),
                }}
                days={{
                    name: "dueInDays",
                    label: t("input:filter_form.due_in_days"),
                    placeholder: t("placeholder:filter_form.due_in_days"),
                    initialValue: expenseFilter.dueInDays,
                    onChange: () => formInstance.validateFields(["dueInDays"]),
                    rules: rules.dueInDays,
                }}
                formInstance={formInstance}
            />

            <FormMinMaxDate
                header={t("input:request.date_paid_at")}
                min={{
                    name: "minPaidAtDate",
                    label: t("input:expense_filter.min_date"),
                    placeholder: t("placeholder:expense_filter.min_date"),
                    initialValue: expenseFilter.minPaidAtDate,
                }}
                max={{
                    name: "maxPaidAtDate",
                    label: t("input:expense_filter.max_date"),
                    placeholder: t("placeholder:expense_filter.max_date"),
                    initialValue: expenseFilter.maxPaidAtDate,
                }}
                formInstance={formInstance}
            />

            <FormSelectTags
                header={t("input:expense_filter.expenseTags")}
                placeholder={t("input:filter_form.not_set")}
                initialValue={expenseFilter.expenseTags}
                name="expenseTags"
            />

            <FormSelect
                name="costCenter"
                header={t("input:expense_filter.cost_center")}
                initialValue={expenseFilter.costCenter}
                options={getDynamicCostCenterList(options.costCenters as Array<CostCenter>, showCostCentersIds)}
                isCollapse
                showSearch
                fallback
            />

            {(options.costCenters2?.length ?? 0) > 0 && (
                <FormSelect
                    name="costCenter2"
                    header={t("input:filter_form.cost_center_2")}
                    initialValue={expenseFilter.costCenter2}
                    options={getDynamicCostCenter2List(options.costCenters2 as Array<CostCenter2>)}
                    isCollapse
                    showSearch
                    fallback
                />
            )}

            {(options.expenseAccounts?.length ?? 0) > 0 && (
                <FormSelect
                    name="expenseAccount"
                    header={t("input:filter_form.expense_account")}
                    initialValue={expenseFilter.expenseAccount}
                    options={getDynamicExpenseAccountList(options.expenseAccounts as Array<ExpenseAccount>)}
                    isCollapse
                    showSearch
                    fallback
                />
            )}

            {(AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__READ) || AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__TEAM__READ)) && (
                <FormSelect
                    name="requestedBy"
                    header={t("input:expense_filter.requester")}
                    initialValue={expenseFilter.requestedBy}
                    options={options.employees ?? []}
                    displayImages
                    isCollapse
                    showSearch
                    fallback
                />
            )}

            <FormSelect
                name="approvalNeededBy"
                header={t("input:expense_filter.approver")}
                initialValue={expenseFilter.approvalNeededBy}
                options={employeeList}
                displayImages
                isCollapse
                showSearch
                fallback
            />

            <CollapseWrapper keyCode="" header={t("input:filter_form.vendor")} isCollapse isActive={isVendorFilterSelectActive}>
                <VendorSelect
                    shouldShowOnlyVerifiedVendors={false}
                    shouldRenderSelectDropdown={true}
                    selectedVendorType={expenseFilter?.creditorUser ? VendorTypeEnum.USER : VendorTypeEnum.VENDOR}
                    userCreditors={options.userCreditors}
                    selectedCreditorId={expenseFilter?.vendor ?? expenseFilter?.creditorUser}
                    formProps={{ name: "vendor", form: formInstance }}
                    onSelect={onCreditorChange}
                />
            </CollapseWrapper>

            <FormSelect
                name="exportFormat"
                header={t("input:expense_filter.export_format.title")}
                isCollapse
                initialValue={expenseFilter.exportFormat}
                options={exportOptions}
                fallback
            />
        </Form>
    )
}
